import { StyleSheet, Text, View } from 'react-native';
import Head from 'expo-router/head';

import { Screen } from '~/components/Screen';
import { TopNav } from '~/components/TopNav';
import terms from '~/lib/termsContent.json';
import { colors, fontFamily, radii, space, type } from '~/ui/tokens';

type TermsBlock = { type: string; text: string };

function Blocks({ items }: { items: TermsBlock[] }) {
    return items.map((item, index) => item.type === 'bullet' ? (
        <View key={index} style={styles.bulletRow}>
            <Text style={styles.bulletDot}>•</Text>
            <Text style={styles.bulletText}>{item.text}</Text>
        </View>
    ) : (
        <Text key={index} style={styles.copy}>{item.text}</Text>
    ));
}

export default function TermsScreen() {
    return (
        <Screen>
            <Head>
                <title>Terms of Service — SuiteScanner</title>
                <meta name="description" content="Terms governing SuiteScanner hotel searches, bookings, payments and cancellations." />
            </Head>
            <TopNav back />
            <View style={styles.body}>
                <View style={styles.intro}>
                    <Text accessibilityRole="header" style={styles.heading}>Terms of Service</Text>
                    <Text style={styles.updated}>Last updated 7 October 2026</Text>
                    <Blocks items={terms.intro} />
                </View>

                <View style={styles.summary}>
                    <Text accessibilityRole="header" style={styles.summaryTitle}>The important things to know</Text>
                    <Blocks items={terms.summary} />
                </View>

                {terms.sections.map((section) => (
                    <View key={section.id} nativeID={`terms-section-${section.id}`} style={styles.section}>
                        <Text accessibilityRole="header" style={styles.sectionTitle}>{section.id}. {section.title}</Text>
                        <Blocks items={section.blocks} />
                    </View>
                ))}
            </View>
        </Screen>
    );
}

const styles = StyleSheet.create({
    body: { width: '100%', maxWidth: 820, alignSelf: 'center', paddingHorizontal: space.card, paddingTop: space.cardLarge, paddingBottom: space.sectionMajor, gap: space.cardLarge },
    intro: { gap: space.related },
    heading: { fontFamily: fontFamily.bold, ...type.pageTitle, color: colors.ink },
    updated: { fontFamily: fontFamily.regular, fontSize: type.metadata.fontSize, color: colors.inkFaint, marginBottom: space.related },
    summary: { gap: space.related, padding: space.card, borderRadius: radii.lg, backgroundColor: colors.skyTint },
    summaryTitle: { fontFamily: fontFamily.semibold, ...type.subsectionTitle, color: colors.ink },
    section: { gap: space.related, borderTopWidth: 1, borderTopColor: colors.lineSoft, paddingTop: space.card },
    sectionTitle: { fontFamily: fontFamily.semibold, ...type.subsectionTitle, color: colors.ink, marginBottom: space.related },
    copy: { fontFamily: fontFamily.regular, ...type.bodyRegular, color: colors.inkSoft },
    bulletRow: { flexDirection: 'row', gap: space.related, alignItems: 'flex-start' },
    bulletDot: { fontFamily: fontFamily.semibold, ...type.bodyRegular, color: colors.brand },
    bulletText: { flex: 1, fontFamily: fontFamily.regular, ...type.bodyRegular, color: colors.inkSoft },
});
